import { useCallback, type FC, type ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';

// components
import { HpSharedCarouselTabs } from '../HpSharedCarouselTabs/HpSharedCarouselTabs.tsx';
import { trendingNowTitle } from '../hpMessages.tsx';
import {
    BESTSELLING_DESIGNS,
    EXCELLENT_VALUES,
    MOST_SAVED_ITEMS,
    tabMap,
    tabNames,
    type TabName,
} from './tabConstants.ts';
import { HpSharedMostSavedItemsCarousel } from '../HpSharedMostSavedItemsCarousel/HpSharedMostSavedItemsCarousel.tsx';
import { HpSharedExcellentValuesCarousel } from '../HpSharedExcellentValuesCarousel/HpSharedExcellentValuesCarousel.tsx';
import { HpSharedBestsellingDesignsCarousel } from '../HpSharedBestsellingDesignsCarousel/HpSharedBestsellingDesignsCarousel.tsx';

// types
import { type HpSharedTrendingNowCarouselTabs_viewer$key } from './__generated__/HpSharedTrendingNowCarouselTabs_viewer.graphql.ts';
import { type HpSharedTrendingNowCarouselTabs_user$key } from './__generated__/HpSharedTrendingNowCarouselTabs_user.graphql.ts';

const viewerFragment = graphql`
    fragment HpSharedTrendingNowCarouselTabs_viewer on Viewer {
        ...HpSharedMostSavedItemsCarousel_viewer
        ...HpSharedExcellentValuesCarousel_viewer
        ...HpSharedBestsellingDesignsCarousel_viewer
    }
`;

const userFragment = graphql`
    fragment HpSharedTrendingNowCarouselTabs_user on User {
        ...HpSharedMostSavedItemsCarousel_user
        ...HpSharedExcellentValuesCarousel_user
        ...HpSharedBestsellingDesignsCarousel_user
    }
`;

const panelMap = {
    [MOST_SAVED_ITEMS]: HpSharedMostSavedItemsCarousel,
    [EXCELLENT_VALUES]: HpSharedExcellentValuesCarousel,
    [BESTSELLING_DESIGNS]: HpSharedBestsellingDesignsCarousel,
};

export const HpSharedTrendingNowCarouselTabs: FC<{
    viewer: HpSharedTrendingNowCarouselTabs_viewer$key | null;
    user?: HpSharedTrendingNowCarouselTabs_user$key | null;
    isMobile: boolean;
}> = ({ viewer: viewerRef, user: userRef, isMobile }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);

    const renderPanel = useCallback(
        (tabName: TabName): ReactNode => {
            const PanelComponent = panelMap[tabName];
            return (
                <PanelComponent viewer={viewer} user={user} isMobile={isMobile} addTopGap={false} />
            );
        },
        [viewer, user, isMobile]
    );

    return (
        <HpSharedCarouselTabs
            tabList={tabNames}
            tabMap={tabMap}
            title={trendingNowTitle}
            isMobile={isMobile}
            renderPanel={renderPanel}
            defaultActiveTab={tabNames[0]}
        />
    );
};
